import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, timeAgo, formatApiError } from "../lib/api";
import Pager from "./Pager";

const EMPTY = { active: false, headline: "", subtext: "", offer_label: "", offer_detail: "", promo_code: "", cta_label: "" };

export function JoinOffers() {
  const [offers, setOffers] = useState(null);
  const [claims, setClaims] = useState({});
  const [claimsPage, setClaimsPage] = useState({});
  const [saving, setSaving] = useState("");

  const load = () => api.get("/admin/join-offers").then((r) => {
    const by = {};
    r.data.forEach((o) => { by[o.role] = { ...EMPTY, ...o }; });
    setOffers(by);
  }).catch(() => toast.error("Couldn't load join pages"));

  const loadClaims = (role, page = 1) => {
    api.get(`/admin/join-offers/${role}/claims`, { params: { page } })
      .then((r) => { setClaims((c) => ({ ...c, [role]: r.data })); setClaimsPage((p) => ({ ...p, [role]: page })); })
      .catch(() => {});
  };

  useEffect(() => { load(); loadClaims("candidate"); loadClaims("employer"); }, []);

  const save = async (role) => {
    setSaving(role);
    try {
      await api.put(`/admin/join-offers/${role}`, offers[role]);
      toast.success("Saved");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(""); }
  };

  if (!offers) return <div className="h-48 rounded-md bg-zinc-50 animate-pulse" data-testid="joinoffers-loading" />;

  return (
    <div className="grid lg:grid-cols-2 gap-5" data-testid="joinoffers">
      {["candidate", "employer"].map((role) => {
        const o = offers[role];
        const set = (k, v) => setOffers((s) => ({ ...s, [role]: { ...s[role], [k]: v } }));
        const url = `https://hiredtoday.co.uk/join/${role === "candidate" ? "candidates" : "employers"}`;
        const cl = claims[role];
        return (
          <div key={role} className="bg-white border border-zinc-200 rounded-lg p-5" data-testid={`joinoffer-${role}`}>
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-display text-base font-bold text-zinc-950">{role === "candidate" ? "Jobseekers" : "Employers"}</h3>
              <label className="flex items-center gap-2 text-xs font-semibold text-zinc-600 cursor-pointer" title="When live, the page is public at the link below">
                <input type="checkbox" checked={o.active} onChange={(e) => set("active", e.target.checked)} data-testid={`joinoffer-${role}-active`} className="accent-zinc-900" />
                Page live
              </label>
            </div>
            <p className="text-xs text-zinc-500 mb-4">
              Share this link: <a href={url} target="_blank" rel="noreferrer" className="text-blue-700 font-medium hover:underline" data-testid={`joinoffer-${role}-link`}>{url}</a>
              {o.claims > 0 && <span className="ml-2 font-semibold text-zinc-700" data-testid={`joinoffer-${role}-claimcount`}>· {o.claims} joined</span>}
            </p>
            <div className="space-y-3">
              <div>
                <label className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">Headline</label>
                <input value={o.headline} onChange={(e) => set("headline", e.target.value)} data-testid={`joinoffer-${role}-headline`}
                  placeholder={role === "candidate" ? "Your next role, minus the search." : "Hiring? Meet people ready to start."}
                  className="mt-1 w-full h-9 px-3 text-sm border border-zinc-300 rounded-md focus:outline-none focus:border-zinc-900" />
              </div>
              <div>
                <label className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">Subtext</label>
                <textarea value={o.subtext} onChange={(e) => set("subtext", e.target.value)} rows={2} data-testid={`joinoffer-${role}-subtext`}
                  placeholder="A sentence or two about why they should join."
                  className="mt-1 w-full px-3 py-2 text-sm border border-zinc-300 rounded-md focus:outline-none focus:border-zinc-900" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider" title="Shown as the offer heading, e.g. '50% off your first listing'">Offer headline</label>
                  <input value={o.offer_label} onChange={(e) => set("offer_label", e.target.value)} data-testid={`joinoffer-${role}-offerlabel`}
                    placeholder="50% off your first listing"
                    className="mt-1 w-full h-9 px-3 text-sm border border-zinc-300 rounded-md focus:outline-none focus:border-zinc-900" />
                </div>
                <div>
                  <label className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider" title="Must match an active code from the Promo codes tab, it's attached to their account when they join">Promo code (optional)</label>
                  <input value={o.promo_code} onChange={(e) => set("promo_code", e.target.value.toUpperCase())} data-testid={`joinoffer-${role}-promo`}
                    placeholder="LAUNCH50"
                    className="mt-1 w-full h-9 px-3 text-sm border border-zinc-300 rounded-md focus:outline-none focus:border-zinc-900" />
                </div>
              </div>
              <div>
                <label className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">Offer small print</label>
                <textarea value={o.offer_detail} onChange={(e) => set("offer_detail", e.target.value)} rows={2} data-testid={`joinoffer-${role}-offerdetail`}
                  placeholder="How it works, any conditions, when it expires."
                  className="mt-1 w-full px-3 py-2 text-sm border border-zinc-300 rounded-md focus:outline-none focus:border-zinc-900" />
              </div>
              <div className="flex items-center justify-between pt-1">
                <button onClick={() => save(role)} disabled={saving === role} data-testid={`joinoffer-${role}-save`}
                  className="h-9 px-5 bg-zinc-950 text-white text-xs font-semibold rounded-md hover:bg-zinc-800 disabled:opacity-50 transition-colors duration-200">
                  {saving === role ? "Saving…" : "Save"}
                </button>
                {o.updated_at && <span className="text-[11px] text-zinc-400">Saved {timeAgo(o.updated_at)}</span>}
              </div>
            </div>
            {cl && cl.total > 0 && (
              <div className="mt-4 border-t border-zinc-100 pt-3" data-testid={`joinoffer-${role}-claims`}>
                <p className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider mb-2">Who joined via this page</p>
                <div className="max-h-40 overflow-y-auto space-y-1">
                  {cl.items.map((u) => (
                    <p key={u.id} className="text-xs text-zinc-600 flex justify-between gap-2">
                      <span className="truncate">{u.name}{u.company_name ? ` · ${u.company_name}` : ""} <span className="text-zinc-400">{u.email}</span></span>
                      <span className="text-zinc-400 shrink-0">{timeAgo(u.joined)}</span>
                    </p>
                  ))}
                </div>
                <Pager page={claimsPage[role] || 1} pages={cl.pages} onPage={(p) => loadClaims(role, p)} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
